import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useStore } from '../../../commons/hooks';
import useLoader from 'hooks/useLoader';
import Swal from 'sweetalert2';

export type AdminDashboardOnClickType =
    | 'NAVIGATE_TO_SERVER_DASHBOARD_DETAIL'
    | 'NAVIGATE_TO_ADMIN_SERVER_CONTROL';

function AdminDashboardViewModel() {
    const history = useHistory();
    const location = useLocation();
    const screenLoader = useLoader();
    const { serverConfigStore, authentication } = useStore();

    // Mapping: tab id -> top-level route path
    const TAB_PATHS: Record<string, string> = {
        '1': '/server',
        '2': '/dbworker',
        '5': '/dbcomparison',
        '6': '/updatelog',
        '7': '/cleartransaction',
        '8': '/migration',
        '9': '/transportuat',
        '10': '/transportprd',
        '11': '/monitorperformance',
        '12': '/f1apilog',
        '13': '/webmonitor',
        '14': '/fapp',
        '15': '/sftp',
        '16': '/portconfig',
        '17': '/monitorsizetable',
        '18': '/migrationscript',
        '19': '/dmsdashboard',
        '20': '/workflowdashboard',
        '21': '/newmigration',
        '22': '/workflowwaitingtime',
        '23': '/f1apiportal',
        '24': '/teamcalendar'
    };

    // Reverse mapping: route path -> tab id
    const PATH_TO_TAB: Record<string, string> = Object.entries(TAB_PATHS).reduce(
        (acc, [key, value]) => ({ ...acc, [value]: key }),
        {} as Record<string, string>
    );

    const monitorServers = serverConfigStore.monitorServers;
    const authenticationUser = authentication.masterEmployeeResponse;

    const resolveTabFromPath = (pathname: string): string => {
        if (PATH_TO_TAB[pathname]) {
            return PATH_TO_TAB[pathname];
        }
        return '0';
    };

    const [activeTab, setActiveTab] = useState(() => resolveTabFromPath(location.pathname));

    useEffect(() => {
        const resolved = resolveTabFromPath(location.pathname);
        setActiveTab(resolved);
    }, [location.pathname]);

    const [autoRefresh, setAutoRefresh] = useState(false);

    const [refresh, setRefresh] = useState(false);
    const toggleRefresh = () => setRefresh(!refresh);

    const [favorites, setFavorites] = useState<string[]>([]);

    useEffect(() => {
        const savedFavorites = localStorage.getItem('adminDashboardFavorites');
        if (savedFavorites) {
            try {
                setFavorites(JSON.parse(savedFavorites));
            } catch (e) {
                console.log('save favorite menu failed:', e);
            }
        }
    }, []);

    const toggleFavorite = (id: string, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }

        let newFavorites: string[] = [];

        if (favorites.includes(id)) {
            newFavorites = favorites.filter(favId => favId !== id);
        } else {
            newFavorites = [...favorites, id];
        }

        setFavorites(newFavorites);
        localStorage.setItem('adminDashboardFavorites', JSON.stringify(newFavorites));
    };

    useEffect(() => {
        if (location.pathname === '/server') {
            _fetchMonitorServers();
            _autoRefreshTime(false);
        }
    }, [refresh]);

    const onSelectTab = (tabNumber: string) => {
        setActiveTab(tabNumber);
        const path = TAB_PATHS[tabNumber];
        if (path) {
            history.push(path);
        } else {
            // tab '0' = Home
            history.push('/admindashboard');
        }
    };

    const onchangeSwitch = (checked: boolean) => {
        if (checked === true) {
            setAutoRefresh(true);
            _autoRefreshTime(true);
        } else {
            setAutoRefresh(false);
        }
    };

    const handleClick = (event: AdminDashboardOnClickType, value: string) => {
        switch (event) {
            case 'NAVIGATE_TO_ADMIN_SERVER_CONTROL': {
                _navigateToAdminServerControl(value);

                break;
            }
            case 'NAVIGATE_TO_SERVER_DASHBOARD_DETAIL': {
                _navigateToServerDashboardDetail(value);

                break;
            }
            default:
                break;
        }
    };

    const _fetchMonitorServers = async () => {
        try {
            screenLoader.show();

            await serverConfigStore.fetchMonitorServers();
        } catch (error) {
            console.log('error', error);

            if (error instanceof Error) {
                Swal.fire('เกิดข้อผิดพลาด!', error.message, 'error');
            }
        } finally {
            screenLoader.hide();
        }
    };

    const _autoRefreshTime = (isRefresh: boolean) => {
        if (autoRefresh || isRefresh) {
            setTimeout(() => {
                toggleRefresh();
            }, 60000);
        }
    };

    const _navigateToServerDashboardDetail = (id: string) => {
        try {
            // set selected server
            serverConfigStore.setSelectedServer(id);

            // redirect to server detail
            history.push(`/ViewDashboardServer/adminServer/${id}`);
        } catch (error) {
            if (error instanceof Error) {
                _handleErrorNavigation(error.message);
            }
        }
    };

    const _navigateToAdminServerControl = (id: string) => {
        try {
            // set selected server
            serverConfigStore.setSelectedServer(id);

            // redirect to server control
            history.push(`/admindashboardcontrol/${id}`);
        } catch (error) {
            if (error instanceof Error) {
                _handleErrorNavigation(error.message);
            }
        }
    };

    const _handleErrorNavigation = (message: string) => {
        Swal.fire('เกิดข้อผิดพลาด!', message, 'error').then(result => {
            if (result.isConfirmed) {
                history.push('/');
            }
        });
    };

    return {
        activeTab,
        onSelectTab,
        handleClick,
        monitorServers,
        onchangeSwitch,
        autoRefresh,
        toggleRefresh,
        authenticationUser,
        favorites,
        toggleFavorite
    };
}

export default AdminDashboardViewModel;
